import React from 'react';
import {
  ArrowLeft,
  Star,
  Download,
  ShoppingCart,
  Play,
  HardDrive,
  User,
  Building2,
  Ban,
  Loader2,
  MoreVertical,
  Link2,
  RotateCcw,
  Trash2,
  ArrowUpCircle,
  Monitor,
  Apple,
  Terminal,
  Globe,
  Shield,
  Languages,
  CalendarDays,
  Sparkles,
  Users,
  Trophy,
  Map,
  Zap,
  Puzzle,
  Palette,
  HelpCircle,
  MessageSquarePlus,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { useGamingStore, GameHighlight, GameScreenshot } from '../../store/gamingStore';
import { useBillingStore } from '../../store/billingStore';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';

const platformLabels: Record<string, string> = {
  windows: 'Windows',
  macos_intel: 'macOS Intel',
  macos_silicon: 'macOS Apple Silicon',
  linux: 'Linux',
  all: 'Toutes plateformes',
};

const platformIcons: Record<string, typeof Monitor> = {
  windows: Monitor,
  macos_intel: Apple,
  macos_silicon: Apple,
  linux: Terminal,
};

const highlightIconMap: Record<string, typeof Sparkles> = {
  sparkles: Sparkles,
  users: Users,
  trophy: Trophy,
  map: Map,
  zap: Zap,
  shield: Shield,
  puzzle: Puzzle,
  palette: Palette,
};

function formatMB(mb?: number | null) {
  if (!mb || mb <= 0) return 'N/A';
  if (mb >= 1000) return `${(mb / 1000).toFixed(1)} GB`;
  return `${mb} MB`;
}

function formatBytes(bytes?: number | null) {
  if (!bytes || bytes <= 0) return null;
  const mb = Math.round(bytes / 1_048_576);
  return formatMB(mb);
}

function formatDate(value?: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString('fr-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function screenshotUrl(item: string | GameScreenshot) {
  return typeof item === 'string' ? item : item.url;
}

function screenshotPlatform(item: string | GameScreenshot) {
  return typeof item === 'string' ? 'all' : item.platform || 'all';
}

function currentDesktopPlatform() {
  const electronApi = (window as any).api;
  if (!electronApi?.platform) return null;
  if (electronApi.platform === 'win32') return 'windows';
  if (electronApi.platform === 'linux') return 'linux';
  if (electronApi.platform === 'darwin') {
    return electronApi.arch === 'arm64' ? 'macos_silicon' : 'macos_intel';
  }
  return null;
}

function youtubeEmbedUrl(url?: string | null) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtube.com')) {
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (parsed.hostname.includes('youtu.be')) {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

function hasRequirementValues(group?: Record<string, string | null>) {
  return !!group && Object.values(group).some(Boolean);
}

function RequirementColumn({
  title,
  values,
}: {
  title: string;
  values?: Record<string, string | null>;
}) {
  const rows = [
    ['OS', values?.os],
    ['CPU', values?.cpu],
    ['RAM', values?.ram],
    ['GPU', values?.gpu],
    ['Stockage', values?.storage],
  ].filter(([, value]) => value);

  if (rows.length === 0) {
    return (
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-text-muted mb-3">{title}</h4>
        <p className="text-sm text-text-muted">Non spécifiée</p>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-xs font-bold uppercase tracking-wider text-text-muted mb-3">{title}</h4>
      <div className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-4 text-sm">
            <span className="text-text-muted">{label}</span>
            <span className="text-text-primary text-right">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HighlightIcon({ highlight }: { highlight: GameHighlight }) {
  const Icon = highlightIconMap[highlight.icon || 'sparkles'] || Sparkles;
  return <Icon className="h-5 w-5 text-gaming-accent" />;
}

const GameDetail: React.FC = () => {
  const {
    selectedGame, isLoadingGame, goBackFromDetail, purchaseGame,
    libraryGames, downloadGame, downloadingGameId, downloadedGameSlugs,
    openDownloadedGame, reviewingStoreVersionId, downloadReviewBuild,
    createGameShortcut, uninstallGame, gameDownloads,
    gameUpdatesAvailable, updateGame, installedGameVersions,
    submitReview, openGameDetail,
  } = useGamingStore();

  const [showGameMenu, setShowGameMenu] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState<'uninstall' | 'reinstall' | null>(null);
  const [reviewRating, setReviewRating] = React.useState(5);
  const [reviewTitle, setReviewTitle] = React.useState('');
  const [reviewContent, setReviewContent] = React.useState('');
  const [reviewSaving, setReviewSaving] = React.useState(false);
  const [selectedMedia, setSelectedMedia] = React.useState(0);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowGameMenu(false);
      }
    };
    if (showGameMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGameMenu]);

  if (isLoadingGame || !selectedGame) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gaming-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const game = selectedGame;
  const isOwned = libraryGames.some(g => g.id === game.id) || !!game.is_owned;
  const isDownloaded = !!downloadedGameSlugs[game.slug];
  const isReviewMode = !!reviewingStoreVersionId;
  const effectivePrice = Number(game.sale_price ?? game.price ?? 0);
  const isFree = !!game.is_free || effectivePrice === 0;
  const activeDownload = gameDownloads.find(
    (d) => d.gameSlug === game.slug && (d.status === 'downloading' || d.status === 'verifying')
  );
  const isActivelyDownloading = !!downloadingGameId && (downloadingGameId === game.id || downloadingGameId === String(reviewingStoreVersionId)) || !!activeDownload;
  const updateInfo = gameUpdatesAvailable[game.slug];
  const hasUpdate = isDownloaded && !!updateInfo && !isReviewMode;
  const embedUrl = youtubeEmbedUrl(game.trailer_url);
  const detectedPlatform = currentDesktopPlatform();
  const screenshots = (game.screenshots || []).filter((shot) => !!screenshotUrl(shot));
  const platformScreenshots = screenshots.filter((shot) => {
    const platform = screenshotPlatform(shot);
    return platform === 'all' || !detectedPlatform || platform === detectedPlatform;
  });
  const mediaShots = platformScreenshots.length > 0 ? platformScreenshots : screenshots;
  const selectedShot = mediaShots[selectedMedia] || mediaShots[0];
  const currentPlatformInfo = game.platforms?.find((p) => p.platform === detectedPlatform) || game.platforms?.[0];
  const displaySize = formatBytes(currentPlatformInfo?.file_size) || formatMB(game.file_size_mb);
  const releaseDate = formatDate(game.latest_store_version?.released_at || game.release_date);
  const latestVersion = game.latest_store_version?.version || game.version;
  const latestChangelog = game.latest_store_version?.changelog;
  const requirementsPlatform = currentPlatformInfo?.platform || detectedPlatform || Object.keys(game.requirements || {})[0];
  const currentRequirements = requirementsPlatform ? game.requirements?.[requirementsPlatform] : null;
  const ratingDistribution = game.review_summary?.distribution || {};
  const totalReviews = Number(game.review_summary?.count ?? game.rating_count ?? 0);
  const ratingAverage = Number(game.review_summary?.average ?? game.rating_avg ?? 0);
  const isElectron = !!window.api;

  const downloadStageLabel = () => {
    if (!activeDownload) return 'Téléchargement...';
    if (activeDownload.status === 'verifying') return 'Installation...';
    return 'Téléchargement...';
  };

  const desktopRequiredAction = (
    <button
      type="button"
      disabled
      className="flex items-center gap-2 bg-bg-secondary border border-border-color text-text-muted px-5 py-2.5 rounded-lg font-bold text-sm cursor-not-allowed"
      title="Installation is available in Erozion Desktop."
    >
      <Download className="h-4 w-4" />
      Erozion Desktop requis
    </button>
  );

  const handlePurchase = async () => {
    const price = game.sale_price ?? game.price;
    if (Number(price) === 0 || game.is_free) {
      await purchaseGame(game.id);
      return;
    }
    useBillingStore.getState().openPaymentDrawer({
      type: 'game_purchase',
      itemId: game.id,
      itemName: game.title,
      amount: Number(price),
      currency: 'cad',
      imageUrl: game.banner_image_url || game.cover_image_url,
      description: game.short_description || undefined,
    });
  };

  const handleReviewSubmit = async () => {
    setReviewSaving(true);
    const ok = await submitReview(game.id, reviewRating, reviewTitle || undefined, reviewContent || undefined);
    if (ok) {
      setReviewTitle('');
      setReviewContent('');
    }
    setReviewSaving(false);
  };

  const primaryAction = (isReviewMode || isOwned) && !isElectron ? desktopRequiredAction : isReviewMode ? (
    isDownloaded ? (
      <button
        onClick={() => openDownloadedGame(game.slug, { reviewStoreVersionId: reviewingStoreVersionId! })}
        className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-lg font-bold text-sm transition-colors hover:bg-white/90"
      >
        <Play className="h-4 w-4 fill-black" />
        Lancer
      </button>
    ) : (
      <button
        onClick={() => downloadReviewBuild(reviewingStoreVersionId!)}
        disabled={isActivelyDownloading}
        className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-lg font-bold text-sm transition-colors hover:bg-white/90 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isActivelyDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {isActivelyDownloading ? downloadStageLabel() : 'Télécharger'}
      </button>
    )
  ) : isOwned ? (
    isDownloaded ? (
      <>
        <button
          onClick={() => openDownloadedGame(game.slug, { gameId: game.id })}
          className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-lg font-bold text-sm transition-colors hover:bg-white/90"
        >
          <Play className="h-4 w-4 fill-black" />
          Lancer
        </button>
        {hasUpdate && (
          <button
            onClick={() => updateGame(game.id, game.slug)}
            disabled={isActivelyDownloading}
            className="flex items-center gap-2 bg-gaming-accent hover:bg-gaming-accent-hover text-black px-4 py-2.5 rounded-lg font-bold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isActivelyDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpCircle className="h-4 w-4" />}
            {isActivelyDownloading ? downloadStageLabel() : 'Mettre à jour'}
          </button>
        )}
      </>
    ) : (
      <button
        onClick={() => downloadGame(game.id, game.slug)}
        disabled={isActivelyDownloading}
        className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-lg font-bold text-sm transition-colors hover:bg-white/90 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isActivelyDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {isActivelyDownloading ? downloadStageLabel() : 'Télécharger'}
      </button>
    )
  ) : (
    <button
      onClick={handlePurchase}
      className="flex items-center gap-2 bg-gaming-accent hover:bg-gaming-accent-hover text-black px-6 py-2.5 rounded-lg font-bold text-sm transition-colors"
    >
      <ShoppingCart className="h-4 w-4" />
      {isFree ? 'Obtenir gratuitement' : (
        <>
          Acheter: {game.sale_price !== null && game.sale_price !== undefined ? (
            <span className="flex items-center gap-1">
              <span className="line-through opacity-60">{Number(game.price).toFixed(2)}$</span>
              {Number(game.sale_price).toFixed(2)}$
            </span>
          ) : `${Number(game.price).toFixed(2)}$`}
        </>
      )}
    </button>
  );

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar bg-bg-primary">
      <div className="relative min-h-[360px] overflow-hidden">
        {game.banner_image_url ? (
          <img src={game.banner_image_url} alt={game.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gaming-accent/25 to-bg-tertiary" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/70 to-bg-primary/20" />
        <button
          onClick={goBackFromDetail}
          className="absolute top-4 left-4 z-10 flex items-center gap-2 text-sm text-white/80 hover:text-white bg-black/40 hover:bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg transition-all"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-56 relative z-10 pb-10">
        {!!game.is_suspended && (
          <div className="mb-4 flex items-center gap-3 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3">
            <Ban className="h-5 w-5 text-red-400 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-400">Ce jeu a été retiré de la boutique</p>
              <p className="text-xs text-text-secondary mt-0.5">Ce jeu n&apos;est plus disponible au téléchargement.</p>
            </div>
          </div>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 items-end">
          <div className="w-56 lg:w-full max-w-[320px]">
            <div className="aspect-[2/3] rounded-lg overflow-hidden shadow-2xl border border-border-color bg-bg-secondary">
              {game.cover_image_url ? (
                <img src={game.cover_image_url} alt={game.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gaming-accent/20 to-bg-tertiary flex items-center justify-center">
                  <span className="text-5xl font-black text-gaming-accent/40">{game.title.charAt(0)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-0 pb-2">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {game.genre && <span className="text-xs font-semibold text-gaming-accent bg-gaming-accent/10 px-2.5 py-1 rounded">{game.genre}</span>}
              {game.age_rating && <span className="text-xs font-semibold text-text-secondary bg-bg-secondary/80 border border-border-color px-2.5 py-1 rounded">{game.age_rating}</span>}
              {isOwned && <span className="text-xs font-semibold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2.5 py-1 rounded">Acquis</span>}
              {game.sale_price !== null && game.sale_price !== undefined && Number(game.price) > 0 && (
                <span className="text-xs font-bold text-black bg-gaming-accent px-2.5 py-1 rounded">
                  -{Math.round(((Number(game.price) - Number(game.sale_price)) / Number(game.price)) * 100)}%
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-black text-text-primary leading-tight">{game.title}</h1>
            {game.subtitle && <p className="text-lg text-text-secondary mt-2 max-w-3xl">{game.subtitle}</p>}
            {game.short_description && <p className="text-sm text-text-secondary mt-4 max-w-3xl leading-relaxed">{game.short_description}</p>}

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-text-secondary mt-5">
              {(game.studio?.name || game.developer) && (
                <span className="flex items-center gap-1.5">
                  <User className="h-4 w-4" />
                  {game.studio?.name || game.developer}
                  {game.studio?.is_verified && <CheckCircle2 className="h-3.5 w-3.5 text-gaming-accent" />}
                </span>
              )}
              {game.publisher && (
                <span className="flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" />
                  {game.publisher}
                </span>
              )}
              {releaseDate && (
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" />
                  {releaseDate}
                </span>
              )}
              {ratingAverage > 0 && (
                <span className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  {ratingAverage.toFixed(1)} ({totalReviews} avis)
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-7">
              {primaryAction}

              {isElectron && (isOwned || isReviewMode) && isDownloaded && (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setShowGameMenu((v) => !v)}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-bg-secondary border border-border-color text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {showGameMenu && (
                    <div className="absolute top-full right-0 mt-1 w-52 bg-bg-secondary border border-border-color rounded-lg shadow-xl z-50 py-1 overflow-hidden">
                      <button
                        onClick={() => { setShowGameMenu(false); createGameShortcut(game.slug, { title: game.title, ...(isReviewMode ? { reviewStoreVersionId: reviewingStoreVersionId! } : {}) }); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        Créer un raccourci
                      </button>
                      <button
                        onClick={() => { setShowGameMenu(false); setConfirmAction('reinstall'); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Réinstaller
                      </button>
                      <div className="border-t border-border-color my-1" />
                      <button
                        onClick={() => { setShowGameMenu(false); setConfirmAction('uninstall'); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Désinstaller
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
              <div className="bg-bg-secondary border border-border-color rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Taille</p>
                <p className="text-sm font-semibold text-text-primary flex items-center gap-1.5">
                  <HardDrive className="h-3.5 w-3.5 text-gaming-accent" />
                  {displaySize}
                </p>
              </div>
              <div className="bg-bg-secondary border border-border-color rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Version</p>
                <p className="text-sm font-semibold text-text-primary">
                  {hasUpdate ? (
                    <span className="flex items-center gap-1">
                      <span className="text-text-muted">{installedGameVersions[game.slug] || '?'}</span>
                      <span className="text-gaming-accent">→</span>
                      <span className="text-gaming-accent">{updateInfo}</span>
                    </span>
                  ) : latestVersion || 'N/A'}
                </p>
              </div>
              <div className="bg-bg-secondary border border-border-color rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Téléchargements</p>
                <p className="text-sm font-semibold text-text-primary flex items-center gap-1.5">
                  <Download className="h-3.5 w-3.5 text-gaming-accent" />
                  {Number(game.download_count || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-bg-secondary border border-border-color rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Plateformes</p>
                <div className="flex items-center gap-1.5">
                  {(game.platforms || []).slice(0, 4).map((p) => {
                    const Icon = platformIcons[p.platform] || Monitor;
                    return <Icon key={p.platform} className="h-4 w-4 text-text-secondary" />;
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {(embedUrl || mediaShots.length > 0) && (
          <section className="mt-10">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-4">
              <div className="aspect-video rounded-lg overflow-hidden bg-black border border-border-color">
                {embedUrl && selectedMedia === 0 ? (
                  <iframe
                    title={`${game.title} trailer`}
                    src={embedUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : selectedShot ? (
                  <img src={screenshotUrl(selectedShot)} alt={`${game.title} screenshot`} className="w-full h-full object-cover" />
                ) : null}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                {embedUrl && (
                  <button
                    onClick={() => setSelectedMedia(0)}
                    className={`aspect-video rounded-lg overflow-hidden border text-left relative ${selectedMedia === 0 ? 'border-gaming-accent' : 'border-border-color'}`}
                  >
                    {game.banner_image_url && <img src={game.banner_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Play className="h-8 w-8 text-white fill-white" />
                    </div>
                  </button>
                )}
                {mediaShots.slice(0, embedUrl ? 5 : 6).map((shot, index) => {
                  const mediaIndex = embedUrl ? index + 1 : index;
                  return (
                    <button
                      key={`${screenshotPlatform(shot)}-${screenshotUrl(shot)}-${index}`}
                      onClick={() => setSelectedMedia(mediaIndex)}
                      className={`aspect-video rounded-lg overflow-hidden border bg-bg-secondary ${selectedMedia === mediaIndex ? 'border-gaming-accent' : 'border-border-color'}`}
                    >
                      <img src={screenshotUrl(shot)} alt="" className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8 mt-10">
          <div className="space-y-8">
            {game.highlights && game.highlights.length > 0 && (
              <section>
                <h2 className="text-xl font-black text-text-primary mb-4">Pourquoi y jouer</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {game.highlights.map((highlight, index) => (
                    <div key={`${highlight.title}-${index}`} className="rounded-lg border border-border-color bg-bg-secondary p-5">
                      <div className="h-10 w-10 rounded-lg bg-gaming-accent/10 flex items-center justify-center mb-4">
                        <HighlightIcon highlight={highlight} />
                      </div>
                      <h3 className="font-bold text-text-primary">{highlight.title}</h3>
                      {highlight.description && <p className="text-sm text-text-secondary leading-relaxed mt-2">{highlight.description}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-lg border border-border-color bg-bg-secondary p-6">
              <h2 className="text-xl font-black text-text-primary mb-4">À propos</h2>
              <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                {game.description || game.short_description || 'Aucune description disponible pour le moment.'}
              </p>
              {game.tags && game.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {game.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-bg-tertiary text-text-secondary px-2.5 py-1 rounded border border-border-color">{tag}</span>
                  ))}
                </div>
              )}
            </section>

            {latestChangelog && (
              <section className="rounded-lg border border-border-color bg-bg-secondary p-6">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <h2 className="text-xl font-black text-text-primary">Nouveautés</h2>
                  {latestVersion && <span className="text-xs text-text-muted">v{latestVersion}</span>}
                </div>
                <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">{latestChangelog}</p>
              </section>
            )}

            {game.faqs && game.faqs.length > 0 && (
              <section className="rounded-lg border border-border-color bg-bg-secondary p-6">
                <h2 className="text-xl font-black text-text-primary mb-4">FAQ</h2>
                <div className="space-y-4">
                  {game.faqs.map((faq, index) => (
                    <div key={`${faq.question}-${index}`} className="border-b border-border-color pb-4 last:border-0 last:pb-0">
                      <h3 className="text-sm font-bold text-text-primary flex items-center gap-2">
                        <HelpCircle className="h-4 w-4 text-gaming-accent" />
                        {faq.question}
                      </h3>
                      <p className="text-sm text-text-secondary leading-relaxed mt-2">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-lg border border-border-color bg-bg-secondary p-6">
              <div className="flex items-center justify-between gap-4 mb-5">
                <h2 className="text-xl font-black text-text-primary">Avis des joueurs</h2>
                {totalReviews > 0 && <span className="text-sm text-text-secondary">{totalReviews} avis</span>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6">
                <div>
                  <div className="text-4xl font-black text-text-primary">{ratingAverage ? ratingAverage.toFixed(1) : 'N/A'}</div>
                  <div className="flex items-center gap-0.5 mt-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-4 w-4 ${i < Math.round(ratingAverage) ? 'text-yellow-400 fill-yellow-400' : 'text-text-muted'}`} />
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = Number(ratingDistribution[String(rating)] || 0);
                    const width = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                    return (
                      <div key={rating} className="flex items-center gap-3 text-xs">
                        <span className="w-8 text-text-muted">{rating}★</span>
                        <div className="h-2 flex-1 rounded-full bg-bg-tertiary overflow-hidden">
                          <div className="h-full bg-yellow-400" style={{ width: `${width}%` }} />
                        </div>
                        <span className="w-8 text-right text-text-muted">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {isOwned && !isReviewMode && (
                <div className="mt-6 rounded-lg bg-bg-tertiary border border-border-color p-4">
                  <h3 className="text-sm font-bold text-text-primary flex items-center gap-2 mb-3">
                    <MessageSquarePlus className="h-4 w-4 text-gaming-accent" />
                    Laisser un avis
                  </h3>
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <button key={i} onClick={() => setReviewRating(i + 1)} className="p-0.5">
                        <Star className={`h-5 w-5 ${i < reviewRating ? 'text-yellow-400 fill-yellow-400' : 'text-text-muted'}`} />
                      </button>
                    ))}
                  </div>
                  <input
                    value={reviewTitle}
                    onChange={(e) => setReviewTitle(e.target.value)}
                    placeholder="Titre de l'avis"
                    className="w-full bg-bg-secondary border border-border-color rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gaming-accent/50 mb-3"
                  />
                  <textarea
                    value={reviewContent}
                    onChange={(e) => setReviewContent(e.target.value)}
                    rows={3}
                    placeholder="Votre avis"
                    className="w-full bg-bg-secondary border border-border-color rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gaming-accent/50 resize-none"
                  />
                  <button
                    onClick={handleReviewSubmit}
                    disabled={reviewSaving}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gaming-accent text-black text-sm font-bold hover:bg-gaming-accent-hover disabled:opacity-60 transition-colors"
                  >
                    {reviewSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Publier l&apos;avis
                  </button>
                </div>
              )}

              {game.reviews && game.reviews.length > 0 && (
                <div className="space-y-4 mt-6">
                  {game.reviews.map(review => (
                    <div key={review.id} className="border-t border-border-color pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-full bg-gaming-accent/20 flex items-center justify-center text-[10px] font-bold text-gaming-accent overflow-hidden">
                          {review.avatar_url ? (
                            <img src={review.avatar_url} alt={review.username} className="w-7 h-7 object-cover" />
                          ) : (
                            review.username.charAt(0).toUpperCase()
                          )}
                        </div>
                        <span className="text-sm font-medium text-text-primary">{review.username}</span>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`h-3 w-3 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-text-muted'}`} />
                          ))}
                        </div>
                      </div>
                      {review.title && <p className="text-sm font-semibold text-text-primary">{review.title}</p>}
                      {review.content && <p className="text-sm text-text-secondary mt-1 leading-relaxed">{review.content}</p>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-6">
            <section className="rounded-lg border border-border-color bg-bg-secondary p-5">
              <h2 className="text-base font-black text-text-primary mb-4">Compatibilité</h2>
              <div className="space-y-3">
                {(game.platforms || []).map((platform) => {
                  const Icon = platformIcons[platform.platform] || Monitor;
                  return (
                    <div key={platform.platform} className="flex items-center justify-between gap-3 text-sm">
                      <span className="flex items-center gap-2 text-text-secondary">
                        <Icon className="h-4 w-4" />
                        {platformLabels[platform.platform] || platform.platform}
                      </span>
                      <span className="text-text-primary">{platform.version || 'À venir'}</span>
                    </div>
                  );
                })}
              </div>
              {(hasRequirementValues(currentRequirements?.minimum) || hasRequirementValues(currentRequirements?.recommended)) && (
                <div className="grid grid-cols-1 gap-5 mt-6 pt-5 border-t border-border-color">
                  <RequirementColumn title="Minimum" values={currentRequirements?.minimum} />
                  <RequirementColumn title="Recommandée" values={currentRequirements?.recommended} />
                </div>
              )}
            </section>

            {(game.supported_languages?.length || game.website_url || game.eula_url || game.privacy_policy_url) && (
              <section className="rounded-lg border border-border-color bg-bg-secondary p-5">
                <h2 className="text-base font-black text-text-primary mb-4">Informations</h2>
                {game.supported_languages && game.supported_languages.length > 0 && (
                  <div className="flex items-start gap-2 text-sm text-text-secondary mb-4">
                    <Languages className="h-4 w-4 mt-0.5 text-gaming-accent" />
                    <span>{game.supported_languages.join(', ')}</span>
                  </div>
                )}
                <div className="space-y-2">
                  {game.website_url && (
                    <a href={game.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-gaming-accent hover:text-gaming-accent-hover">
                      <Globe className="h-4 w-4" />
                      Site officiel
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {game.eula_url && (
                    <a href={game.eula_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary">
                      <Shield className="h-4 w-4" />
                      EULA
                    </a>
                  )}
                  {game.privacy_policy_url && (
                    <a href={game.privacy_policy_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary">
                      <Shield className="h-4 w-4" />
                      Confidentialité
                    </a>
                  )}
                </div>
              </section>
            )}

            {(isOwned || isReviewMode) && (
              <section className="rounded-lg border border-border-color bg-bg-secondary p-5">
                <h2 className="text-base font-black text-text-primary mb-4">Bibliothèque</h2>
                <div className="space-y-2">
                  {!isElectron ? (
                    <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-3 text-xs text-amber-200">
                      L'installation et le lancement des jeux sont disponibles dans Erozion Desktop.
                    </div>
                  ) : isDownloaded ? (
                    <>
                      <button onClick={() => openDownloadedGame(game.slug, isReviewMode ? { reviewStoreVersionId: reviewingStoreVersionId! } : { gameId: game.id })} className="w-full flex items-center justify-center gap-2 bg-white text-black px-4 py-2.5 rounded-lg font-bold text-sm hover:bg-white/90">
                        <Play className="h-4 w-4 fill-black" />
                        Lancer
                      </button>
                      <button onClick={() => createGameShortcut(game.slug, { title: game.title, ...(isReviewMode ? { reviewStoreVersionId: reviewingStoreVersionId! } : {}) })} className="w-full flex items-center justify-center gap-2 bg-bg-tertiary border border-border-color text-text-secondary px-4 py-2.5 rounded-lg text-sm hover:text-text-primary">
                        <Link2 className="h-4 w-4" />
                        Créer un raccourci
                      </button>
                    </>
                  ) : (
                    <button onClick={() => isReviewMode ? downloadReviewBuild(reviewingStoreVersionId!) : downloadGame(game.id, game.slug)} className="w-full flex items-center justify-center gap-2 bg-white text-black px-4 py-2.5 rounded-lg font-bold text-sm hover:bg-white/90">
                      <Download className="h-4 w-4" />
                      Télécharger
                    </button>
                  )}
                </div>
              </section>
            )}

            {game.studio && (
              <section className="rounded-lg border border-border-color bg-bg-secondary p-5">
                <h2 className="text-base font-black text-text-primary mb-4">Studio</h2>
                <div className="flex items-center gap-3">
                  {game.studio.logo_url ? (
                    <img src={game.studio.logo_url} alt="" className="h-12 w-12 rounded-lg object-cover border border-border-color" />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-gaming-accent/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-gaming-accent" />
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-text-primary flex items-center gap-1.5">
                      {game.studio.name || game.developer || 'Studio'}
                      {game.studio.is_verified && <CheckCircle2 className="h-3.5 w-3.5 text-gaming-accent" />}
                    </p>
                    {game.studio.type && <p className="text-xs text-text-muted">{game.studio.type}</p>}
                  </div>
                </div>
                {game.studio.bio && <p className="text-sm text-text-secondary leading-relaxed mt-4">{game.studio.bio}</p>}
                {game.studio.trader && (
                  <div className="mt-4 rounded-lg border border-border-color bg-bg-tertiary p-3 text-xs text-text-secondary space-y-1">
                    <p className="font-bold text-text-primary">Trader information</p>
                    <p>{game.studio.trader.name}</p>
                    <p>{game.studio.trader.email}</p>
                    <p>{game.studio.trader.phone}</p>
                    <p>{game.studio.trader.address}</p>
                  </div>
                )}
                {game.studio.website && (
                  <a href={game.studio.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-gaming-accent hover:text-gaming-accent-hover mt-4">
                    <Globe className="h-4 w-4" />
                    Site du studio
                  </a>
                )}
              </section>
            )}

            {game.more_from_studio && game.more_from_studio.length > 0 && (
              <section className="rounded-lg border border-border-color bg-bg-secondary p-5">
                <h2 className="text-base font-black text-text-primary mb-4">Du même studio</h2>
                <div className="space-y-3">
                  {game.more_from_studio.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => openGameDetail(item.slug)}
                      className="w-full flex items-center gap-3 text-left rounded-lg hover:bg-bg-tertiary transition-colors p-2 -mx-2"
                    >
                      {item.cover_image_url ? (
                        <img src={item.cover_image_url} alt="" className="h-14 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-14 w-10 rounded bg-bg-tertiary" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-text-primary truncate">{item.title}</p>
                        {item.short_description && <p className="text-xs text-text-muted line-clamp-2">{item.short_description}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </aside>
        </div>
      </div>

      <AlertDialog open={confirmAction === 'uninstall'} onOpenChange={(open: boolean) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Désinstaller {game.title} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les fichiers du jeu seront supprimés de votre ordinateur. Vous pourrez le retélécharger à tout moment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => { setConfirmAction(null); uninstallGame(game.slug, game.title); }}
            >
              Désinstaller
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAction === 'reinstall'} onOpenChange={(open: boolean) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Réinstaller {game.title} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le jeu sera retéléchargé et les fichiers actuels seront remplacés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmAction(null); downloadGame(game.id, game.slug); }}>
              Réinstaller
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GameDetail;
